import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { InputOutput } from "./input-output";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";
import { useMemo } from "react";

interface Props {
  sourceNodeId: string;
}

const Variables = ({ sourceNodeId }: Props) => {
  const {
    getNodesVariabes,
    getNodeById,
    getInteroperabilityNodesUpstreamFromIf,
  } = useWorkflowStore();

  const nodes = useMemo(() => {
    return sourceNodeId ? getNodesVariabes(sourceNodeId) : [];
  }, [sourceNodeId, getNodesVariabes]);

  const isOnResultadoBranch = useMemo(() => {
    if (!sourceNodeId) return false;
    const currentNode = getNodeById(sourceNodeId);
    if (!currentNode) return false;

    const nodeData = currentNode.data as unknown as NodeData;
    if (!nodeData.edge) return false;
    const sourceNode = getNodeById(nodeData.edge.source);
    if (!sourceNode) return false;

    const sourceData = sourceNode.data as unknown as NodeData;
    if (
      sourceData.type === NodeTypes.IF &&
      nodeData.edge.source_handler === "true"
    ) {
      return true;
    }

    return false;
  }, [sourceNodeId, getNodeById]);

  const filteredRegularNodes = useMemo(() => {
    if (!sourceNodeId) return [];
    return nodes
      .filter((n) => {
        const data = n.data as unknown as NodeData;
        return data.type === NodeTypes.INITIALIZER || data.inputs.length > 0;
      })
      .filter((n) => {
        const data = n.data as unknown as NodeData;
        if (
          data.type === NodeTypes.SWITCH ||
          data.type === NodeTypes.IF ||
          data.type === NodeTypes.MULTITHREAD ||
          data.type === NodeTypes.NOTIFICATION ||
          data.type === NodeTypes.WAIT
        ) {
          return false;
        }
        if (data.type === NodeTypes.DATA_CENTER) {
          return data.inputs.some(({ name }) => name === "operation");
        }
        if (data.type === NodeTypes.EXPEDIENTE) {
          return data.inputs.some(({ name }) => name === "operation");
        }
        return true;
      });
  }, [nodes, sourceNodeId]);

  const interoperabilityNodes = useMemo(() => {
    if (!sourceNodeId || !isOnResultadoBranch) return [];

    const currentNode = getNodeById(sourceNodeId);
    if (!currentNode) return [];

    const nodeData = currentNode.data as unknown as NodeData;
    if (!nodeData.edge) return [];

    const ifNodeId = nodeData.edge.source;
    const interopNodes = getInteroperabilityNodesUpstreamFromIf(ifNodeId);
    const validInteropNodes = interopNodes.filter((node) => {
      const nodeData = node.data as unknown as NodeData;
      return nodeData.inputs && nodeData.inputs.length > 0;
    });
    const regularNodeIds = new Set(filteredRegularNodes.map((n) => n.id));
    return validInteropNodes.filter((node) => !regularNodeIds.has(node.id));
  }, [
    isOnResultadoBranch,
    sourceNodeId,
    getNodeById,
    getInteroperabilityNodesUpstreamFromIf,
    filteredRegularNodes,
  ]);

  if (!sourceNodeId) {
    return null;
  }

  return (
    <>
      {filteredRegularNodes.map((variable) => (
        <InputOutput
          id={variable.id}
          display_name={
            (variable.data as unknown as NodeData).displayName || ""
          }
          nodeType={
            variable.data.type as (typeof NodeTypes)[keyof typeof NodeTypes]
          }
          key={`variable-${variable.id}`}
        />
      ))}
      {isOnResultadoBranch &&
        interoperabilityNodes.map((interopNode) => (
          <InputOutput
            id={interopNode.id}
            display_name={
              (interopNode.data as unknown as NodeData).displayName || ""
            }
            nodeType={NodeTypes.INTEROPERABILITY}
            key={`interop-variable-${interopNode.id}`}
          />
        ))}
    </>
  );
};

export default Variables;
